import React, { useState, useEffect } from 'react';
import { EmpreendimentoCreate, FaseEmpreendimentoEnum, TipoEmpreendimento, TipoParteEmpreendimento } from '../../types/empreendimento';
import { TipoParteEmpreendimentoService } from '../../services/empreendimentoService';
import GerenciarItensEmpreendimento from './GerenciarItensEmpreendimento';
import GerenciarTiposParteEmpreendimento from './GerenciarTiposParteEmpreendimento';
import Select from 'react-select';
import ItensEmpreendimentoSelector from './componentes/ItensEmpreendimentoSelector';
import UnidadeEditorModal from './componentes/form-especifico-parts/UnidadeEditorModal';
import TorresTabs from './componentes/form-especifico-parts/TorresTabs';
import TorreView from './componentes/form-especifico-parts/TorreView';
import UnidadesAvulsasView from './componentes/form-especifico-parts/UnidadesAvulsasView';
import MultiImageUpload from "./componentes/ImageUpload";
import { Plus } from 'lucide-react';
import { Unidade, Torre, UnitRef, RowRef, inputStyle, primaryButtonStyle, secondaryButtonStyle, successButtonStyle } from './componentes/form-especifico-parts/types';

interface EmpreendimentoFormEspecificoProps {
    basicData: EmpreendimentoCreate;
    /** Se a etapa 1 (Informações Básicas) já está válida — usado pra travar
     * o botão "Finalizar" até TODAS as etapas estarem ok, não só essa. */
    basicDataValid: boolean;
    onSave: (formattedData: any, rawData: any) => void;
    onBack?: (specificData?: any) => void;
    initialSpecificData?: any;
    /** Reporta o sub-passo interno (1: Fase/Tipo/Unidades, 2: Imagens/Documentos)
     * pro wizard pai (NovoEmpreendimento) refletir na barra de progresso —
     * antes esse avanço acontecia escondido, sem nenhum indicador visual. */
    onStepChange?: (step: 1 | 2) => void;
    /** Comando do pai pra pular direto pra um sub-passo (clique na barra de
     * progresso) sem passar pela validação normal de "Próxima Etapa" — a barra
     * agora permite navegação livre mesmo com campos não preenchidos.
     * `token` incrementa a cada clique, mesmo pedindo o mesmo `step` de novo —
     * sem isso, clicar duas vezes seguidas na mesma etapa (depois que o filho
     * já navegou por conta própria via "Próxima Etapa"/"Voltar") não fazia
     * nada, porque o React descarta um setState com valor idêntico ao atual. */
    requestedStep?: { step: 1 | 2; token: number };
    /** Disparado quando o envio final é bloqueado pela validação de unidades
     * (nenhuma unidade cadastrada / unidades incompletas), pra o pai destacar
     * em vermelho a etapa correspondente na barra de progresso. */
    onValidationError?: () => void;
    /** Reporta continuamente se os sub-passos internos (Unidades / Imagens e
     * Documentos) já estão válidos — pra o pai colorir os círculos 2 e 3 da
     * barra de progresso em vermelho (incompleto) ou verde (ok). */
    onValidityChange?: (step2Valid: boolean, step3Valid: boolean) => void;
    /** Reporta o formData interno (torres/unidades/tipo/itens) a cada mudança
     * — igual ao onDataChange que EmpreendimentoFormBasico já tem pros campos
     * básicos. Usado pelo pai só pra autosave de rascunho local; NUNCA é
     * realimentado de volta como initialSpecificData (isso criaria um loop:
     * o pai recebe, devolve como prop, o efeito de seed dispara de novo,
     * gera um novo formData, reporta de novo...). initialSpecificData
     * continua sendo só semente (IA ou rascunho carregado na abertura).
     * Os arquivos (book/tabela/extras) não entram aqui — são File, não dá
     * pra persistir; só os nomes, pra um aviso de "reanexe ao retomar". */
    onDataChange?: (rawData: any, arquivosNomes: { book?: string; tabelaPreco?: string; extra1?: string; extra2?: string }) => void;
}

const tiposDeEmpreendimento = [
    { value: 1, label: 'Lote' },
    { value: 2, label: 'Apartamento' },
    { value: 3, label: 'Comercial' },
    { value: 4, label: 'Casa' },
    { value: 5, label: 'Compacto' },
    { value: 6, label: 'Empresarial' },
];

const EmpreendimentoFormEspecifico: React.FC<EmpreendimentoFormEspecificoProps> = ({
    basicData,
    basicDataValid,
    onSave,
    onBack,
    initialSpecificData,
    onStepChange,
    requestedStep,
    onValidationError,
    onValidityChange,
    onDataChange
}) => {
    const [formData, setFormData] = useState({
        fase_empreendimento: FaseEmpreendimentoEnum.PLANTA,
        tipo_empreendimento: [] as number[],
        item_empreendimento: [] as number[],
        torres: [] as Torre[],
        unidades_sem_torre: [] as Unidade[],
    });

    const [files, setFiles] = useState<(File | null)[]>(Array(3).fill(null));
    const [previews, setPreviews] = useState<(string | null)[]>(Array(3).fill(null));
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [step, setStep] = useState(1);
    const [pdfFileName, setPdfFileName] = useState<string | null>(null);
    const [bookFile, setBookFile] = useState<File | null>(null);
    const [bookFileName, setBookFileName] = useState<string | null>(null);
    const [arquivoExtra1, setArquivoExtra1] = useState<File | null>(null);
    const [arquivoExtra1Name, setArquivoExtra1Name] = useState<string | null>(null);
    const [arquivoExtra2, setArquivoExtra2] = useState<File | null>(null);
    const [arquivoExtra2Name, setArquivoExtra2Name] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [geracaoEmMassa, setGeracaoEmMassa] = useState({ andares: '', unidadesPorAndar: '' });
    const [hasInteractedWithTowers, setHasInteractedWithTowers] = useState(false);
    const [selectedUnidade, setSelectedUnidade] = useState<UnitRef | null>(null);
    const [unidadeTempData, setUnidadeTempData] = useState<Unidade | null>(null);
    const [showItensModal, setShowItensModal] = useState(false);
    // Incrementa toda vez que o modal "Gerenciar Itens" fecha, forçando o
    // ItensEmpreendimentoSelector a recarregar a lista (senão um item recém
    // criado/editado/excluído só aparecia depois de recarregar a página).
    const [itensRefreshSignal, setItensRefreshSignal] = useState(0);
    const [showTiposParteModal, setShowTiposParteModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const tipoParteEmpreendimentoService = new TipoParteEmpreendimentoService();
    const [sourceUnit, setSourceUnit] = useState<UnitRef | null>(null);
    const [selectedUnits, setSelectedUnits] = useState<Map<string, UnitRef>>(new Map());
    // Modelo por linha: uma linha (andar) inteira serve de molde para outras linhas.
    // Convive com o modelo por unidade acima, mas os dois nunca ficam ativos juntos.
    const [sourceRow, setSourceRow] = useState<RowRef | null>(null);
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [tiposCache, setTiposCache] = useState<Record<number, TipoParteEmpreendimento>>({});

    const isLote = () => formData.tipo_empreendimento.includes(1);

    useEffect(() => {
        onStepChange?.(step as 1 | 2);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    useEffect(() => {
        onDataChange?.(formData, {
            book: bookFileName ?? undefined,
            tabelaPreco: pdfFileName ?? undefined,
            extra1: arquivoExtra1Name ?? undefined,
            extra2: arquivoExtra2Name ?? undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, bookFileName, pdfFileName, arquivoExtra1Name, arquivoExtra2Name]);

    // Clique direto na barra de progresso do pai: pula pro sub-passo pedido
    // sem rodar validateFormData, mesmo que a etapa atual esteja incompleta.
    useEffect(() => {
        if (requestedStep && requestedStep.step !== step) {
            setStep(requestedStep.step);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedStep?.token]);

    useEffect(() => {
        const tipos = formData.tipo_empreendimento;
        const hasTowerType = tipos.some(t => [2, 3, 5].includes(t));
        const isLoteCasa = tipos.includes(1) && tipos.includes(4);

        if (hasTowerType) {
            // Mesma guarda do ramo isLoteCasa logo abaixo: se veio dado da IA, ela é
            // quem decide se torres fica vazio ou não (ex.: tabela sem nenhuma torre
            // identificada, tudo em unidades_sem_torre) — sem isso, esse efeito
            // "ajudava" recriando uma Torre 1 vazia por cima do que a IA importou,
            // no mesmo ciclo de render em que os dados da IA acabavam de chegar.
            if (formData.torres.length === 0 && !hasInteractedWithTowers && !initialSpecificData) {
                setFormData(prev => ({ ...prev, torres: [{ nome: 'Torre 1', unidades: [] }] }));
            }
        } else if (isLoteCasa) {
            if (!hasInteractedWithTowers && !initialSpecificData) {
                setFormData(prev => ({ ...prev, torres: [] }));
            }
        }
    }, [formData.tipo_empreendimento, formData.torres.length, hasInteractedWithTowers, initialSpecificData]);
    
    useEffect(() => {
        if (initialSpecificData) {
            setFormData(prev => ({ ...prev, ...initialSpecificData, torres: initialSpecificData.torres || [], unidades_sem_torre: initialSpecificData.unidades_sem_torre || [] }));
        }
    }, [initialSpecificData]);

    useEffect(() => {
        const loadTipoDefinition = async () => {
            const tiposToFetch: number[] = [];
            if (unidadeTempData?.tipo_parte_empreendimento && Array.isArray(unidadeTempData.tipo_parte_empreendimento)) {
                unidadeTempData.tipo_parte_empreendimento.forEach(tipoId => {
                    const numericId = Number(tipoId);
                    if (!isNaN(numericId) && !tiposCache[numericId]) {
                        tiposToFetch.push(numericId);
                    }
                });
            }
            if (tiposToFetch.length > 0) {
                try {
                    const fetchedTipos = await Promise.all(tiposToFetch.map(id => tipoParteEmpreendimentoService.getTipoById(id)));
                    const newCache = fetchedTipos.reduce((acc, tipo) => {
                        if (tipo && tipo.id) acc[Number(tipo.id)] = tipo;
                        return acc;
                    }, {} as Record<number, any>);
                    setTiposCache(prev => ({ ...prev, ...newCache }));
                } catch (error) {
                    console.error("Erro ao carregar definições de tipos de parte:", error);
                }
            }
        };
        loadTipoDefinition();
    }, [unidadeTempData?.tipo_parte_empreendimento]);
    
    const validateUnidade = (unidade: Unidade): { isValid: boolean; isPartial: boolean } => {
        if (!unidade) return { isValid: false, isPartial: false };
        // area_privativa_m2/valor_imovel podem legitimamente ser "0.00" (unidade
        // sem preço definido, ex.: importada por IA com disponibilidade
        // "Bloqueada") — !!'0.00' já é true, mas !!0 (número) seria false, então
        // checa presença em vez de truthiness pra não excluir esse caso.
        const base = !!unidade.nome
            && unidade.area_privativa_m2 !== undefined && unidade.area_privativa_m2 !== ''
            && unidade.valor_imovel !== undefined && unidade.valor_imovel !== '';
        if (!base) return { isValid: false, isPartial: true };

        // valor_avaliacao é obrigatório: financiamento/garantia
        const temAvaliacao = unidade.valor_avaliacao !== undefined && unidade.valor_avaliacao !== '';
        if (!temAvaliacao) return { isValid: false, isPartial: true };

        // vagas_rotativas (fixa + aleatória) é opcional — há unidades sem garagem
        // tipo_parte_empreendimento (a "característica" da unidade, ex.: "Apartamento
        // Padrão") é opcional de verdade: o backend cria um tipo "Padrão"
        // automaticamente quando não vem preenchido (ver UnidadeSerializer.create no
        // backend). Bloquear a unidade por causa desse campo — que a importação por
        // IA nunca preenche, pois não tem correspondência no documento fonte —
        // marcava TODA unidade importada como inválida mesmo com os dados corretos.
        return { isValid: true, isPartial: true };
    };

    // Versão enxuta (só booleano, sem mensagens) da checagem de unidades —
    // usada pra colorir o círculo da etapa 2 na barra do pai em tempo real,
    // sem esperar uma tentativa de envio. validateFormData (abaixo) continua
    // sendo a fonte das mensagens de erro detalhadas mostradas em tela.
    const getUnitsSummary = (): { isValid: boolean; hasUnits: boolean } => {
        let hasUnits = false;
        let allUnitsOk = true;
        const checkUnit = (unidade: Unidade) => {
            if (unidade && Object.keys(unidade).length > 1) {
                hasUnits = true;
                const { isPartial, isValid } = validateUnidade(unidade);
                if (isPartial && !isValid) allUnitsOk = false;
                if (unidade.confirmada === false) allUnitsOk = false;
            }
        };
        formData.torres.forEach(torre => torre.unidades.forEach(andar => andar.forEach(checkUnit)));
        formData.unidades_sem_torre.forEach(checkUnit);
        return { isValid: hasUnits && allUnitsOk, hasUnits };
    };

    // Etapa 2 (Unidades e Configurações): precisa de tipo selecionado (campo
    // obrigatório no JSX) e de unidades válidas. Etapa 3 (Imagens e
    // Documentos): só a tabela de preço é obrigatória (mesmo "required" do
    // input de arquivo abaixo) — book/extras são opcionais.
    const step2Valid = getUnitsSummary().isValid && formData.tipo_empreendimento.length > 0;
    const step3Valid = pdfFile !== null;

    useEffect(() => {
        onValidityChange?.(step2Valid, step3Valid);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step2Valid, step3Valid]);

     const validateFormData = async (): Promise<{ isValid: boolean; errors: string[] }> => {
        const validationErrors: string[] = [];
        if (!basicData.nome?.trim()) validationErrors.push("Nome do empreendimento é obrigatório");
        let hasUnits = false;
        let hasUnconfirmedUnits = false;
        const checkUnit = (unidade: Unidade, location: string) => {
            if (unidade && Object.keys(unidade).length > 1) {
                hasUnits = true;
                const { isPartial, isValid } = validateUnidade(unidade);
                if (isPartial && !isValid) {
                    validationErrors.push(`${location} está com campos incompletos.`);
                }
                if (unidade.confirmada === false) {
                    hasUnconfirmedUnits = true;
                }
            }
        };
        formData.torres.forEach((torre, torreIndex) => {
            torre.unidades.forEach((andar, andarIndex) => {
                andar.forEach((unidade, unidadeIndex) => {
                    checkUnit(unidade, `Torre "${torre.nome}", Unidade no ${torre.unidades.length - andarIndex}º Andar`);
                });
            });
        });
        formData.unidades_sem_torre.forEach((unidade, index) => {
            checkUnit(unidade, `Unidade Avulsa "${unidade.nome || `item ${index + 1}`}"`);
        });

        if (hasUnconfirmedUnits) {
            validationErrors.push("Existem unidades que precisam de confirmação. Verifique as unidades em amarelo antes de prosseguir.");
        }

        if (!hasUnits) {
            validationErrors.push("Pelo menos uma unidade deve ser cadastrada");
        }
        
        return { isValid: validationErrors.length === 0, errors: validationErrors };
    };
    
    const formatDataForSubmission = () => {
        const imagensParaEnviar = files.filter(file => file !== null);
        const partesDasTorres = formData.torres.flatMap(torre => torre.unidades.flat().filter(u => u && Object.keys(u).length > 1));
        const partesAvulsas = formData.unidades_sem_torre.filter(u => u && Object.keys(u).length > 1);
        const todasAsPartes = [...partesDasTorres, ...partesAvulsas];
        return {
            ...basicData,
            fase_empreendimento: formData.fase_empreendimento,
            tipo_empreendimento: formData.tipo_empreendimento,
            item_empreendimento: formData.item_empreendimento,
            partes_empreendimento: todasAsPartes.map(parte => ({
                ...parte,
                nome: `${basicData.nome} - ${parte.nome}`,
                area_privativa_m2: parseFloat(String(parte.area_privativa_m2).replace(',', '.')) || '0.00',
                valor_imovel: parseFloat(String(parte.valor_imovel).replace(',', '.')) || '0.00',
                ...(parte.valor_avaliacao !== undefined && parte.valor_avaliacao !== '' && {
                    valor_avaliacao: parseFloat(String(parte.valor_avaliacao).replace(',', '.')) || '0.00',
                }),
                tipo_parte_empreendimento: isLote() ? [] : (Array.isArray(parte.tipo_parte_empreendimento) ? parte.tipo_parte_empreendimento : (parte.tipo_parte_empreendimento ? [parte.tipo_parte_empreendimento] : [])),
            })),
            imagens: imagensParaEnviar,
            tabela_preco: pdfFile,
            book: bookFile,
            arquivo_extra_1: arquivoExtra1,
            arquivo_extra_2: arquivoExtra2,
        };
    };

    const handleUnitSelection = (key: string, ref: UnitRef) => {
        const newSelection = new Map(selectedUnits);
        if (newSelection.has(key)) newSelection.delete(key);
        else newSelection.set(key, ref);
        setSelectedUnits(newSelection);
    };

    const handleSetSource = () => {
        if (selectedUnits.size !== 1) return alert("Por favor, selecione exatamente uma unidade para usar como modelo.");
        const ref = selectedUnits.values().next().value;
        setSourceUnit(ref || null);
        setSelectedUnits(new Map());
    };

    const handleApplyToSelected = () => {
        if (!sourceUnit || selectedUnits.size === 0) return alert("Selecione um modelo e pelo menos uma unidade de destino.");
        setFormData(prev => {
            const newFormData = JSON.parse(JSON.stringify(prev));
            const sourceUnitData = sourceUnit.type === 'torre'
                ? newFormData.torres[sourceUnit.torreIndex].unidades[sourceUnit.andarIndex][sourceUnit.unidadeIndex]
                : newFormData.unidades_sem_torre[sourceUnit.unidadeIndex];
            
            selectedUnits.forEach(ref => {
                if (ref.type === 'torre') {
                    const originalUnit = newFormData.torres[ref.torreIndex].unidades[ref.andarIndex][ref.unidadeIndex];
                    newFormData.torres[ref.torreIndex].unidades[ref.andarIndex][ref.unidadeIndex] = { ...sourceUnitData, torre: originalUnit.torre, andar: originalUnit.andar, numero_apartamento: originalUnit.numero_apartamento, confirmada: false };
                } else {
                    newFormData.unidades_sem_torre[ref.unidadeIndex] = { ...sourceUnitData, torre: 0, andar: 0, numero_apartamento: 0, confirmada: false };
                }
            });
            return newFormData;
        });
        handleClearSelection();
    };
    
    const handleClearSelection = () => {
        setSelectedUnits(new Map());
        setSourceUnit(null);
        setSourceRow(null);
        setSelectedRows(new Set());
    };

    const unitKeyOf = (ref: UnitRef) =>
        ref.type === 'torre'
            ? `t${ref.torreIndex}-a${ref.andarIndex}-u${ref.unidadeIndex}`
            : `avulsa-${ref.unidadeIndex}`;

    const isSourceUnit = (ref: UnitRef) =>
        !!sourceUnit && unitKeyOf(sourceUnit) === unitKeyOf(ref);

    // Marca/desmarca todas as unidades de um andar. Se o andar já estiver
    // inteiramente selecionado, o clique desmarca — é um toggle.
    const handleSelectRow = (andarIndex: number) => {
        const andar = formData.torres[activeTab]?.unidades[andarIndex];
        if (!andar) return;
        const refs: UnitRef[] = andar.map((_, unidadeIndex) => ({
            type: 'torre', torreIndex: activeTab, andarIndex, unidadeIndex,
        }));
        toggleRefs(refs);
    };

    // Mesma ideia para uma coluna (a "Unid. N" de todos os andares). Andares mais
    // curtos simplesmente não têm essa coluna e são ignorados.
    const handleSelectColumn = (unidadeIndex: number) => {
        const torre = formData.torres[activeTab];
        if (!torre) return;
        const refs: UnitRef[] = [];
        torre.unidades.forEach((andar, andarIndex) => {
            if (unidadeIndex < andar.length) {
                refs.push({ type: 'torre', torreIndex: activeTab, andarIndex, unidadeIndex });
            }
        });
        toggleRefs(refs);
    };

    const toggleRefs = (refs: UnitRef[]) => {
        const selecionaveis = refs.filter(ref => !isSourceUnit(ref));
        if (selecionaveis.length === 0) return;
        const todasJaSelecionadas = selecionaveis.every(ref => selectedUnits.has(unitKeyOf(ref)));
        const novaSelecao = new Map(selectedUnits);
        selecionaveis.forEach(ref => {
            const key = unitKeyOf(ref);
            if (todasJaSelecionadas) novaSelecao.delete(key);
            else novaSelecao.set(key, ref);
        });
        setSelectedUnits(novaSelecao);
    };

    // --- Modelo por linha ---------------------------------------------------
    const handleSetSourceRow = (andarIndex: number) => {
        setSourceUnit(null);
        setSelectedUnits(new Map());
        setSelectedRows(new Set());
        setSourceRow({ torreIndex: activeTab, andarIndex });
    };

    const handleToggleTargetRow = (andarIndex: number) => {
        if (!sourceRow) return;
        if (sourceRow.torreIndex === activeTab && sourceRow.andarIndex === andarIndex) return;
        const novas = new Set(selectedRows);
        if (novas.has(andarIndex)) novas.delete(andarIndex);
        else novas.add(andarIndex);
        setSelectedRows(novas);
    };

    const handleSelectAllRowsInTower = () => {
        if (!sourceRow) return;
        const torre = formData.torres[activeTab];
        if (!torre) return;
        const novas = new Set<number>();
        torre.unidades.forEach((_, andarIndex) => {
            const ehModelo = sourceRow.torreIndex === activeTab && sourceRow.andarIndex === andarIndex;
            if (!ehModelo) novas.add(andarIndex);
        });
        setSelectedRows(novas);
    };

    // Copia a linha-modelo para cada linha selecionada, igualando o número de
    // unidades: cria as que faltam e descarta as que sobram. torre/andar/número
    // do apartamento são recalculados depois pelo resync, porque derivam da posição.
    const handleApplyRowToSelected = () => {
        if (!sourceRow || selectedRows.size === 0) {
            return alert('Selecione uma linha como modelo e pelo menos uma linha de destino.');
        }

        const destinoComDados = Array.from(selectedRows).some(andarIndex => {
            const andar = formData.torres[activeTab]?.unidades[andarIndex] || [];
            return andar.some(u => u?.confirmada);
        });
        if (destinoComDados && !window.confirm(
            'Alguma linha de destino tem unidades já confirmadas, que serão substituídas pelo modelo. Continuar?'
        )) return;

        setFormData(prev => {
            let novasTorres: Torre[] = JSON.parse(JSON.stringify(prev.torres));
            const modelo: Unidade[] = novasTorres[sourceRow.torreIndex]?.unidades[sourceRow.andarIndex] || [];

            selectedRows.forEach(andarIndex => {
                const destino = novasTorres[activeTab]?.unidades[andarIndex];
                if (!destino) return;
                novasTorres[activeTab].unidades[andarIndex] = modelo.map(unidadeModelo => ({
                    ...JSON.parse(JSON.stringify(unidadeModelo)),
                    confirmada: false,
                }));
            });

            novasTorres = resyncUnidadeNumeros(novasTorres, activeTab);
            return { ...prev, torres: novasTorres };
        });

        setSourceRow(null);
        setSelectedRows(new Set());
    };

    // --- Reordenação --------------------------------------------------------
    const moveAndar = (from: number, to: number) => {
        if (from === to || activeTab >= formData.torres.length) return;
        setFormData(prev => {
            let novasTorres: Torre[] = JSON.parse(JSON.stringify(prev.torres));
            const andares = novasTorres[activeTab].unidades;
            if (from < 0 || from >= andares.length || to < 0 || to >= andares.length) return prev;
            const [movido] = andares.splice(from, 1);
            andares.splice(to, 0, movido);
            novasTorres = resyncUnidadeNumeros(novasTorres, activeTab);
            return { ...prev, torres: novasTorres };
        });
        handleClearSelection();
    };

    const moveTorre = (from: number, to: number) => {
        if (from === to) return;
        setHasInteractedWithTowers(true);
        setFormData(prev => {
            let novasTorres: Torre[] = JSON.parse(JSON.stringify(prev.torres));
            if (from < 0 || from >= novasTorres.length || to < 0 || to >= novasTorres.length) return prev;
            const [movida] = novasTorres.splice(from, 1);
            novasTorres.splice(to, 0, movida);
            novasTorres = resyncTodasAsTorres(novasTorres);
            return { ...prev, torres: novasTorres };
        });
        // A aba ativa acompanha a torre que foi arrastada.
        setActiveTab(to);
        handleClearSelection();
    };

    // Arrastar uma unidade sobre outra troca as duas de posição (cada uma leva
    // seus próprios dados). Arrastar sobre uma célula vazia move a unidade para
    // lá, deixando a linha de origem mais curta. Em ambos os casos, andar/torre/
    // número do apartamento são recalculados pelo resync a partir da nova posição.
    const moveUnidadeCell = (from: UnitRef, to: UnitRef) => {
        if (from.type !== 'torre' || to.type !== 'torre') return;
        if (from.torreIndex !== to.torreIndex) return;
        if (from.andarIndex === to.andarIndex && from.unidadeIndex === to.unidadeIndex) return;

        setFormData(prev => {
            let novasTorres: Torre[] = JSON.parse(JSON.stringify(prev.torres));
            const torre = novasTorres[from.torreIndex];
            const linhaOrigem = torre?.unidades[from.andarIndex];
            const linhaDestino = torre?.unidades[to.andarIndex];
            if (!linhaOrigem || !linhaDestino || from.unidadeIndex >= linhaOrigem.length) return prev;

            const unidadeOrigem = linhaOrigem[from.unidadeIndex];
            const destinoOcupado = to.unidadeIndex < linhaDestino.length;

            if (destinoOcupado) {
                const unidadeDestino = linhaDestino[to.unidadeIndex];
                linhaOrigem[from.unidadeIndex] = unidadeDestino;
                linhaDestino[to.unidadeIndex] = unidadeOrigem;
            } else {
                linhaOrigem.splice(from.unidadeIndex, 1);
                linhaDestino.push(unidadeOrigem);
            }

            novasTorres = resyncUnidadeNumeros(novasTorres, from.torreIndex);
            return { ...prev, torres: novasTorres };
        });
        handleClearSelection();
    };

    const handleSelectAllInTower = () => {
        if (activeTab >= formData.torres.length) return;
        const newSelection = new Map<string, UnitRef>();
        formData.torres[activeTab].unidades.forEach((andar, andarIndex) => {
            andar.forEach((unit, unidadeIndex) => {
                const ref: UnitRef = { type: 'torre', torreIndex: activeTab, andarIndex, unidadeIndex };
                const key = `t${ref.torreIndex}-a${ref.andarIndex}-u${ref.unidadeIndex}`;
                const isSource = sourceUnit?.type === 'torre' && sourceUnit.torreIndex === ref.torreIndex && sourceUnit.andarIndex === ref.andarIndex && sourceUnit.unidadeIndex === ref.unidadeIndex;
                if (!isSource) newSelection.set(key, ref);
            });
        });
        setSelectedUnits(newSelection);
    };
    
    const handleSelectAllAvulsas = () => {
        const newSelection = new Map<string, UnitRef>();
        formData.unidades_sem_torre.forEach((unidade, index) => {
             const ref: UnitRef = { type: 'avulsa', unidadeIndex: index };
             const key = `avulsa-${index}`;
             const isSource = sourceUnit?.type === 'avulsa' && sourceUnit.unidadeIndex === ref.unidadeIndex;
             if(!isSource) newSelection.set(key, ref);
        });
        setSelectedUnits(newSelection);
    };

    const addTorre = () => {
        setHasInteractedWithTowers(true);
        const newTorre = { nome: `Torre ${formData.torres.length + 1}`, unidades: [] };
        setFormData(prev => ({ ...prev, torres: [...prev.torres, newTorre] }));
        setActiveTab(formData.torres.length);
    };

    const removeTorre = (indexToRemove: number) => {
        setHasInteractedWithTowers(true);
        setFormData(prev => ({ ...prev, torres: prev.torres.filter((_, index) => index !== indexToRemove) }));
        if (activeTab >= indexToRemove) setActiveTab(Math.max(0, activeTab - 1));
    };

    const renameTorre = (indice: number, novoNome: string) => {
        setFormData(prev => {
            const novasTorres = [...prev.torres];
            if (novasTorres[indice]) {
                // Atualiza o nome da torre
                novasTorres[indice] = { ...novasTorres[indice], nome: novoNome };
                // Atualiza o torre_texto de todas as unidades desta torre
                novasTorres[indice].unidades = novasTorres[indice].unidades.map(andar =>
                    andar.map(unidade => ({
                        ...unidade,
                        torre_texto: novoNome
                    }))
                );
            }
            return { ...prev, torres: novasTorres };
        });
    };

    const updatePavimentoTexto = (andarIndex: number, novoTexto: string) => {
        setFormData(prev => {
            const novasTorres = [...prev.torres];
            const torreAtiva = novasTorres[activeTab];
            if (torreAtiva && torreAtiva.unidades[andarIndex] && torreAtiva.unidades[andarIndex].length > 0) {
                // Atualiza o pavimento_texto de todas as unidades do andar
                const novoAndar = torreAtiva.unidades[andarIndex].map(u => ({
                    ...u,
                    pavimento_texto: novoTexto
                }));
                torreAtiva.unidades[andarIndex] = novoAndar;
            }
            return { ...prev, torres: novasTorres };
        });
    };

    const openUnidadeEditor = (ref: UnitRef) => {
        const defaultUnidade: Unidade = { nome: '', torre: '', ano_disponibilidade: new Date().getFullYear(), mes_disponibilidade: 1, origem_recurso: 1, area_privativa_m2: '', valor_imovel: '', forma_pagamento: '', tipo_parte_empreendimento: [], atributos_extras: {}, disponibilidade: 1, quantidade_garagem: '', mcmv: false, tipo_produto: 2, andar: '', numero_apartamento: '', vagas_rotativas: ''};
        let currentData: Partial<Unidade> = {};
        if (ref.type === 'torre') {
            currentData = formData.torres[ref.torreIndex]?.unidades[ref.andarIndex]?.[ref.unidadeIndex] || {};
        } else {
            currentData = formData.unidades_sem_torre[ref.unidadeIndex] || {};
        }
        setSelectedUnidade(ref);
        setUnidadeTempData({ ...defaultUnidade, ...currentData });
    };

    const saveUnidade = () => {
        if (!selectedUnidade || !unidadeTempData) return;
        const dataToSave = unidadeTempData.tipo_produto === 1 ? { ...unidadeTempData, tipo_parte_empreendimento: [], atributos_extras: {} } : { ...unidadeTempData };
        dataToSave.confirmada = true;
        setFormData(prev => {
            const newData = JSON.parse(JSON.stringify(prev));
            if (selectedUnidade.type === 'torre') {
                newData.torres[selectedUnidade.torreIndex].unidades[selectedUnidade.andarIndex][selectedUnidade.unidadeIndex] = dataToSave;
            } else {
                newData.unidades_sem_torre[selectedUnidade.unidadeIndex] = dataToSave;
            }
            return newData;
        });
        setSelectedUnidade(null);
        setUnidadeTempData(null);
    };

    const handleUnidadeTempChange = (field: string, value: any) => setUnidadeTempData(prev => prev ? { ...prev, [field]: value } : null);
    const handleAtributoExtraTempChange = (key: string, value: any) => setUnidadeTempData(prev => prev ? { ...prev, atributos_extras: { ...prev.atributos_extras, [key]: value } } : null);
    
    const resyncUnidadeNumeros = (torres: Torre[], torreIndex: number): Torre[] => {
        const novasTorres = JSON.parse(JSON.stringify(torres));
        const torreParaAtualizar = novasTorres[torreIndex];
        if (!torreParaAtualizar) return novasTorres;
        const totalAndares = torreParaAtualizar.unidades.length;
        torreParaAtualizar.unidades.forEach((andar: Unidade[], rowIndex: number) => {
            const andarNumero = totalAndares - rowIndex;
            andar.forEach((unidade: Unidade, colIndex: number) => {
                if (unidade) {
                    unidade.torre = torreIndex + 1;
                    unidade.andar = andarNumero;
                    unidade.numero_apartamento = colIndex + 1;
                }
            });
        });
        return novasTorres;
    };

    // Reordenar torres muda o número de torre de todas as unidades, já que ele é
    // gravado em cada unidade (`torre: activeTab + 1`) e não derivado na hora do
    // envio. Por isso o resync precisa varrer todas as torres, não só uma.
    const resyncTodasAsTorres = (torres: Torre[]): Torre[] => {
        let resultado = torres;
        for (let i = 0; i < torres.length; i++) {
            resultado = resyncUnidadeNumeros(resultado, i);
        }
        return resultado;
    };

    const addAndar = () => {
        if (activeTab >= formData.torres.length) return;
        setHasInteractedWithTowers(true);
        setFormData(prev => {
            const novasTorres = JSON.parse(JSON.stringify(prev.torres));
            novasTorres[activeTab].unidades.unshift([]);
            return { ...prev, torres: novasTorres };
        });
    };
    
    const removeAndar = (andarIndex: number) => {
        if (activeTab >= formData.torres.length) return;
        setFormData(prev => {
            let novasTorres = JSON.parse(JSON.stringify(prev.torres));
            novasTorres[activeTab].unidades = novasTorres[activeTab].unidades.filter((_: any, i: number) => i !== andarIndex);
            novasTorres = resyncUnidadeNumeros(novasTorres, activeTab);
            return { ...prev, torres: novasTorres };
        });
    };

    const addUnidadeToAndar = (andarIndex: number) => {
        if (activeTab >= formData.torres.length) return;
        setHasInteractedWithTowers(true);
        setFormData(prev => {
            const novasTorres = JSON.parse(JSON.stringify(prev.torres));
            const numeroApt = novasTorres[activeTab].unidades[andarIndex].length + 1;
            const totalAndares = novasTorres[activeTab].unidades.length;
            const andarNumero = totalAndares - andarIndex;
            novasTorres[activeTab].unidades[andarIndex].push({ torre: activeTab + 1, andar: andarNumero, numero_apartamento: numeroApt, vagas_rotativas: '' });
            return { ...prev, torres: novasTorres };
        });
    };

    const removeUnidade = (andarIndex: number, unidadeIndex: number) => {
        if (activeTab >= formData.torres.length) return;
        setFormData(prev => {
            let novasTorres = JSON.parse(JSON.stringify(prev.torres));
            novasTorres[activeTab].unidades[andarIndex] = novasTorres[activeTab].unidades[andarIndex].filter((_: any, i: number) => i !== unidadeIndex);
            novasTorres = resyncUnidadeNumeros(novasTorres, activeTab);
            return { ...prev, torres: novasTorres };
        });
    };
    
    const addUnidadeAvulsa = () => setFormData(prev => ({ ...prev, unidades_sem_torre: [...prev.unidades_sem_torre, { torre: 0, andar: 0, numero_apartamento: 0, vagas_rotativas:0 }] }));
    const removeUnidadeAvulsa = (index: number) => setFormData(prev => ({ ...prev, unidades_sem_torre: prev.unidades_sem_torre.filter((_, i) => i !== index) }));
    
    const handleGeracaoEmMassa = () => {
        const andares = parseInt(geracaoEmMassa.andares, 10);
        const unidadesPorAndar = parseInt(geracaoEmMassa.unidadesPorAndar, 10);
        if (isNaN(andares) || isNaN(unidadesPorAndar) || andares <= 0 || unidadesPorAndar <= 0) return alert('Por favor, insira um número válido de andares e unidades por andar.');
        if (formData.torres[activeTab]?.unidades.length > 0) {
            if (!window.confirm('A geração em massa substituirá as unidades existentes nesta torre. Deseja continuar?')) return;
        }
        setHasInteractedWithTowers(true);
        const novasUnidades: Unidade[][] = Array.from({ length: andares }, (_, i) => {
            const andarNumero = andares - i;
            return Array.from({ length: unidadesPorAndar }, (__, j) => ({
                torre: activeTab + 1,
                andar: andarNumero,
                numero_apartamento: j + 1,
            } as Unidade));
        });
        setFormData(prev => {
            const novasTorres = [...prev.torres];
            novasTorres[activeTab] = { ...novasTorres[activeTab], unidades: novasUnidades };
            return { ...prev, torres: novasTorres };
        });
    };

    const getRowCount = (andarIndex: number) => formData.torres[activeTab]?.unidades[andarIndex]?.filter(u => validateUnidade(u).isValid).length || 0;
    const getColCount = (colIndex: number) => formData.torres[activeTab]?.unidades.reduce((count, row) => count + (validateUnidade(row[colIndex]).isValid ? 1 : 0), 0) || 0;
    
    const handleNextStep = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrors([]);
        const { isValid, errors: validationErrors } = await validateFormData();
        if (!isValid) {
            setErrors(validationErrors);
            setLoading(false);
            return;
        }
        setStep(2);
        setLoading(false);
    };

    const handleFinalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrors([]);
        // Antes o envio final ia direto pra API sem checar unidades — só a
        // navegação interna "Próxima Etapa" validava. Isso deixava passar um
        // envio sem nenhuma unidade cadastrada até o backend rejeitar (ou não).
        const { isValid, errors: validationErrors } = await validateFormData();
        if (!isValid) {
            setErrors(validationErrors);
            setStep(1);
            onValidationError?.();
            setLoading(false);
            return;
        }
        const formattedData = formatDataForSubmission();
        try {
            await onSave(formattedData, formData);
        } catch (error: any) {
            const message = error?.response?.data?.message || "Ocorreu um erro ao salvar. Tente novamente.";
            const backendErrors = Array.isArray(message) ? message : [message];
            setErrors(backendErrors);
        } finally {
            setLoading(false);
        }
    };

    const handleBackStep = () => {
        if (step === 2) {
            setStep(1);
        } else if (onBack) {
            onBack(formData);
        }
    };

    const handleFilesChange = (newFiles: (File | null)[]) => {
        setFiles(newFiles);
        setPreviews(newFiles.map(file => (file ? URL.createObjectURL(file) : null)));
    };

    const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setPdfFile(file);
        setPdfFileName(file?.name || null);
    };

    const handleBookChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setBookFile(file);
        setBookFileName(file?.name || null);
    };

    const handleArquivoExtra1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setArquivoExtra1(file);
        setArquivoExtra1Name(file?.name || null);
    };

    const handleArquivoExtra2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setArquivoExtra2(file);
        setArquivoExtra2Name(file?.name || null);
    };

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <form onSubmit={step === 1 ? handleNextStep : handleFinalSubmit} className="space-y-8">
                 {errors.length > 0 && <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md"><div className="ml-3"><h3 className="text-sm font-medium text-red-800">Foram encontrados {errors.length} erros:</h3><div className="mt-2 text-sm text-red-700"><ul className="list-disc pl-5 space-y-1">{errors.map((error, i) => <li key={i}>{error}</li>)}</ul></div></div></div>}
                 {step === 1 && (
                    <>
                        <div className="bg-white shadow-md rounded-lg "><div className="px-6 py-4 bg-gray-50 border-b border-gray-200"><h3 className="text-lg font-semibold text-gray-900">Fase e Tipo do Empreendimento</h3></div><div className="p-6"><div className="grid grid-cols-1 md:grid-cols-2 gap-6"><div><label className="block text-sm font-medium text-gray-700 mb-1">Fase *</label><select value={formData.fase_empreendimento} onChange={e => setFormData(p=>({...p, fase_empreendimento: parseInt(e.target.value)}))} className={inputStyle} required><option value={FaseEmpreendimentoEnum.PLANTA}>Planta</option><option value={FaseEmpreendimentoEnum.OBRAS}>Obras</option><option value={FaseEmpreendimentoEnum.PRONTO}>Pronto</option></select></div><div><label className="block text-sm font-medium text-gray-700 mb-1 ">Tipo *</label><Select isMulti options={tiposDeEmpreendimento} value={tiposDeEmpreendimento.filter(o => formData.tipo_empreendimento.includes(o.value))} onChange={os=>setFormData(p=>({...p,tipo_empreendimento:os?os.map(o=>o.value):[]}))} placeholder="Selecione os tipos" className="react-select-container flex-1" classNamePrefix="react-select"/></div></div></div></div>
                        <ItensEmpreendimentoSelector selectedItens={formData.item_empreendimento} onSelectionChange={ids => setFormData(p => ({...p, item_empreendimento: ids}))} onManageItensClick={() => setShowItensModal(true)} successButtonStyle={successButtonStyle} refreshSignal={itensRefreshSignal}/>
                        <div className="bg-white shadow-md rounded-lg overflow-hidden"><div className="px-6 py-4 bg-gray-50 border-b"><h3 className="text-lg font-semibold">Configuração das Unidades</h3></div><div className="p-6"><div className="flex space-x-2 mb-4"><button type="button" onClick={addTorre} className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700"><Plus size={16}/>Add Torre</button><button type="button" onClick={addUnidadeAvulsa} className="flex items-center gap-2 px-3 py-2 text-sm bg-teal-600 text-white rounded-md shadow-sm hover:bg-teal-700"><Plus size={16}/>Add Unidade Avulsa</button></div><TorresTabs torres={formData.torres} unidadesSemTorreCount={formData.unidades_sem_torre.length} activeTab={activeTab} setActiveTab={setActiveTab} removeTorre={removeTorre} moveTorre={moveTorre} renameTorre={renameTorre} />{activeTab<formData.torres.length?(<TorreView torre={formData.torres[activeTab]} activeTab={activeTab} geracaoEmMassa={geracaoEmMassa} setGeracaoEmMassa={setGeracaoEmMassa} handleGeracaoEmMassa={handleGeracaoEmMassa} selectedUnits={selectedUnits} sourceCoords={sourceUnit} handleSetSource={handleSetSource} handleSelectAllInTower={handleSelectAllInTower} handleApplyToSelected={handleApplyToSelected} handleClearSelection={handleClearSelection} validateUnidade={validateUnidade} handleCellClick={(isSource,key,coords)=>sourceRow?null:isSource?null:sourceUnit?handleUnitSelection(key,coords):openUnidadeEditor(coords)} handleUnitSelection={handleUnitSelection} removeUnidade={removeUnidade} getColCount={getColCount} getRowCount={getRowCount} addUnidadeToAndar={addUnidadeToAndar} removeAndar={removeAndar} addAndar={addAndar} handleSelectRow={handleSelectRow} handleSelectColumn={handleSelectColumn} sourceRow={sourceRow} selectedRows={selectedRows} handleSetSourceRow={handleSetSourceRow} handleToggleTargetRow={handleToggleTargetRow} handleSelectAllRowsInTower={handleSelectAllRowsInTower} handleApplyRowToSelected={handleApplyRowToSelected} moveAndar={moveAndar} moveUnidadeCell={moveUnidadeCell} updatePavimentoTexto={updatePavimentoTexto}/>):(<UnidadesAvulsasView unidades={formData.unidades_sem_torre} validateUnidade={validateUnidade} openUnidadeEditor={openUnidadeEditor} removeUnidadeAvulsa={removeUnidadeAvulsa} selectedUnits={selectedUnits} sourceCoords={sourceUnit} handleUnitSelection={handleUnitSelection} handleSetSource={handleSetSource} handleApplyToSelected={handleApplyToSelected} handleClearSelection={handleClearSelection} handleSelectAllAvulsas={handleSelectAllAvulsas} />)}</div></div>
                    </>
                 )}
                 {step === 2 && (<>
                        <div className="bg-white shadow-md rounded-lg overflow-hidden w-full">
                            <MultiImageUpload label="Imagem Principal" description="Selecione a imagem de destaque do empreendimento." onFilesChange={handleFilesChange} files={files} previewUrls={previews}/>
                        </div>
                        <div className="bg-white shadow-md rounded-lg overflow-hidden w-full mt-6">
                            <div className="px-6 py-4 bg-gray-50 border-b">
                                <h3 className="text-lg font-semibold">Upload da tabela de preço</h3>
                                <p className="mt-1 text-sm text-gray-600">Selecione um PDF.</p>
                            </div>
                            <div className="p-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Tabela de preço</label>
                                <input type="file" accept=".pdf" onChange={handlePdfChange} className={inputStyle} required/>
                                {pdfFileName&&(<p className="mt-2 text-sm text-gray-700">Arquivo: <span className="font-semibold">{pdfFileName}</span></p>)}
                            </div>
                        </div>
                        <div className="bg-white shadow-md rounded-lg overflow-hidden w-full mt-6">
                            <div className="px-6 py-4 bg-gray-50 border-b">
                                <h3 className="text-lg font-semibold">Book do empreendimento</h3>
                                <p className="mt-1 text-sm text-gray-600">Opcional. Cada envio fica registrado nas movimentações, com data.</p>
                            </div>
                            <div className="p-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Book</label>
                                <input type="file" onChange={handleBookChange} className={inputStyle}/>
                                {bookFileName&&(<p className="mt-2 text-sm text-gray-700">Arquivo: <span className="font-semibold">{bookFileName}</span></p>)}
                            </div>
                        </div>
                        <div className="bg-white shadow-md rounded-lg overflow-hidden w-full mt-6">
                            <div className="px-6 py-4 bg-gray-50 border-b">
                                <h3 className="text-lg font-semibold">Arquivos extras</h3>
                                <p className="mt-1 text-sm text-gray-600">Opcionais. Dois slots para documentos adicionais do empreendimento.</p>
                            </div>
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Arquivo extra 1</label>
                                    <input type="file" onChange={handleArquivoExtra1Change} className={inputStyle}/>
                                    {arquivoExtra1Name&&(<p className="mt-2 text-sm text-gray-700">Arquivo: <span className="font-semibold">{arquivoExtra1Name}</span></p>)}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Arquivo extra 2</label>
                                    <input type="file" onChange={handleArquivoExtra2Change} className={inputStyle}/>
                                    {arquivoExtra2Name&&(<p className="mt-2 text-sm text-gray-700">Arquivo: <span className="font-semibold">{arquivoExtra2Name}</span></p>)}
                                </div>
                            </div>
                        </div>
                    </>)}
                <div className="flex justify-between items-center pt-6 border-t"><div>{onBack&&step===1&&(<button type="button" onClick={handleBackStep} className={secondaryButtonStyle}>Voltar</button>)}{step===2&&(<button type="button" onClick={handleBackStep} className={secondaryButtonStyle}>Voltar</button>)}</div><div className="flex space-x-4">{step===1&&(<button type="submit" disabled={loading} className={primaryButtonStyle}>Próxima Etapa</button>)}{step===2&&(<button type="submit" disabled={loading || !basicDataValid || !step2Valid || !step3Valid} title={(!basicDataValid || !step2Valid || !step3Valid) ? 'Preencha todas as etapas (círculo verde na barra) antes de finalizar.' : undefined} className={primaryButtonStyle}>{loading?'Finalizando...':'Finalizar'}</button>)}</div></div>
            </form>
            <UnidadeEditorModal isOpen={!!(selectedUnidade && unidadeTempData)} onClose={() => setSelectedUnidade(null)} onSave={saveUnidade} unidadeTempData={unidadeTempData} handleUnidadeTempChange={handleUnidadeTempChange} handleAtributoExtraTempChange={handleAtributoExtraTempChange} selectedUnidade={selectedUnidade} torres={formData.torres} tiposCache={tiposCache} />
            {showItensModal && <GerenciarItensEmpreendimento onClose={() => { setShowItensModal(false); setItensRefreshSignal(s => s + 1); }} />}
            {showTiposParteModal && <GerenciarTiposParteEmpreendimento onClose={() => setShowTiposParteModal(false)} onTipoSelected={() => { }} />}
        </div>
    );
};

export default EmpreendimentoFormEspecifico;